import React, { useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { ClipLoader } from "react-spinners";
import avatar from "src/assets/images/header/avatar.svg";
import { disputeService } from "src/service/disputeService";

const ResolveModal: React.FC<{
	onClose: () => void;
	fetchDisputeDetails: () => void;
	escrowTrnx: BookingTrnxType[];
	bookingDetails: BookingsTypes;
}> = ({ onClose, escrowTrnx, bookingDetails, fetchDisputeDetails }) => {
	const [selectedFeeId, setSelectedFeeId] = useState<string>("");
	const [amount, setAmount] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string>("");

	const normalTrnx = (() => {
		// Remove 'escrow-' prefix from narration
		const mapped = escrowTrnx.map((item) => ({
			...item,
			narration: item.narration.replace("escrow-", ""),
		}));

		// Find commitment-fee and service-fee
		const commitmentFee = mapped.find(
			(item) => item.narration === "commitment-fee"
		);
		const serviceFeeIndex = mapped.findIndex(
			(item) => item.narration === "service-fee"
		);

		if (commitmentFee && serviceFeeIndex !== -1) {
			// Add commitment-fee value to service-fee value
			mapped[serviceFeeIndex] = {
				...mapped[serviceFeeIndex],
				amount:
					(Number(mapped[serviceFeeIndex].amount) || 0) +
					(Number(commitmentFee.amount) || 0),
			};
			// Remove commitment-fee from the array
			return mapped.filter((item) => item.narration !== "commitment-fee");
		}
		return mapped;
	})();

	const filteredTrnx = escrowTrnx
		.filter((item) => item.narration !== "commitment-fee")
		.filter((item) => item.status === "pending");

	// console.log(filteredTrnx);

	const handleFeeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const feeId = e.target.value;
		setSelectedFeeId(feeId);
		const fee = escrowTrnx.find((item) => item._id === feeId);
		setAmount(fee ? fee.amount.toString() : "");
	};

	const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setAmount(e.target.value);
	};

	const handleRefund = async () => {
		if (!selectedFeeId || !amount || loading) return;
		setLoading(true);
		setError("");
		try {
			const bookingId =
				(bookingDetails as any)._id || (bookingDetails as any).id;
			if (!bookingId) {
				setError("Booking ID not found. Cannot process refund.");
				setLoading(false);
				return;
			}
			const payload = {
				amount: Number(amount),
				booking_escrow_trx_id: selectedFeeId,
			};
			await disputeService.refund(bookingId, payload);
			fetchDisputeDetails();
			onClose();
		} catch (err: any) {
			setError(err?.response?.data?.error?.message || "Refund failed");
		} finally {
			setLoading(false);
		}
	};

	const user = bookingDetails?.user_meta;

	return (
		<div className='w-full md:w-[480px] max-w-[480px] bg-white rounded-2xl p-10 flex flex-col items-center relative shadow-xl '>
			<button
				onClick={onClose}
				type='button'
				className='absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-[#333] hover:bg-[#222] transition-colors'
				aria-label='Close'
			>
				<IoCloseOutline className='text-white text-2xl' />
			</button>
			<h2 className='font-semibold text-3xl text-center mt-2 mb-1'>
				Refund customer
			</h2>
			<p className='text-lg text-gray-500 text-center mb-6'>
				Select the fee to refund
			</p>
			<div className='flex flex-col items-center mb-8'>
				<img
					src={user?.display_picture || avatar}
					alt='avatar'
					className='w-16 h-16 rounded-full object-cover mb-2'
				/>
				<span className='font-semibold text-lg text-gray-700'>
					{user?.first_name} {user?.last_name}
				</span>
			</div>
			<div className='w-full flex flex-col gap-6'>
				<div>
					<label className='block text-gray-600 mb-2'>Select Fee</label>
					<select
						className='w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white'
						value={selectedFeeId}
						onChange={handleFeeChange}
					>
						<option value='' disabled>
							Select Fee
						</option>
						{filteredTrnx?.map((item) => (
							<option key={item._id} value={item._id}>
								{item.narration}
							</option>
						))}
					</select>
				</div>
				<div>
					<label className='block text-gray-600 mb-2'>Enter Amount</label>
					<input
						type='text'
						className='w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary'
						placeholder='₦0.00'
						value={amount}
						onChange={handleAmountChange}
						disabled={!selectedFeeId}
					/>
				</div>
			</div>
			{error && <div className='text-red-500 text-center mt-4'>{error}</div>}
			<button
				onClick={handleRefund}
				disabled={!selectedFeeId || !amount || loading}
				className={`mt-10 w-full py-3 rounded-lg text-lg font-semibold text-white transition-colors ${
					!selectedFeeId || !amount || loading
						? "bg-purple-300 cursor-not-allowed"
						: "bg-[#7607BD] hover:bg-[#5a0596] cursor-pointer"
				}`}
			>
				{loading ? <ClipLoader color='#fff' size={20} /> : "Refund"}
			</button>
		</div>
	);
};

export default ResolveModal;
